/**
 * Remembers which external street-view provider the user last chose, so the
 * StreetViewButton can default to it across every app/panel. Best-effort
 * localStorage persistence: never throws on read/write.
 */
import { getLogger } from '@logtape/logtape';
import { STREET_VIEW_PROVIDERS, type StreetViewProvider } from '#lib/streetView.js';

const log = getLogger(['platform', 'street-view']);
const STORAGE_KEY = 'platform:streetViewProvider';
const DEFAULT_PROVIDER: StreetViewProvider = 'google';

function isProvider(value: unknown): value is StreetViewProvider {
	return STREET_VIEW_PROVIDERS.some((provider) => provider.id === value);
}

function load(): StreetViewProvider {
	if (typeof localStorage === 'undefined') return DEFAULT_PROVIDER;
	const raw = localStorage.getItem(STORAGE_KEY);
	return isProvider(raw) ? raw : DEFAULT_PROVIDER;
}

function createStreetViewStore() {
	let provider = $state<StreetViewProvider>(load());

	return {
		get provider() {
			return provider;
		},
		set(next: StreetViewProvider) {
			provider = next;
			if (typeof localStorage === 'undefined') return;
			try {
				localStorage.setItem(STORAGE_KEY, next);
			} catch (error) {
				log.warn('Could not persist street-view provider: {error}', { error });
			}
		}
	};
}

export const streetViewStore = createStreetViewStore();
